// Copyright (c) 2026 Michael F. Kamprath
//
// UI string resolver.  Flat-keyed lookup by locale with English
// fallback.  Phase 3 slice 3d wired up zh-CN, ja, and ko alongside
// English; partial coverage is intentional -- any key a non-en
// dictionary omits silently falls back to the English value below.
//
// Design choices:
//   - Keys are flat dotted paths ('settings.foo.bar') rather than
//     nested objects, so locale merges are trivial and a missing key
//     in one language can't mask a value in another.
//   - Missing keys return the key itself, not the empty string.  A
//     translator seeing 'settings.foo.bar' in the UI knows exactly
//     what needs adding; an empty string would be a silent hole.
//   - No runtime template substitution in this round.  Interpolation
//     support (`t(key, { name: 'Michael' })`) can land when a locale
//     actually needs it.

import en from './en.js';
import zhCN from './zh-CN.js';
import ja from './ja.js';
import ko from './ko.js';

const DICTIONARIES = {
  en,
  'zh-CN': zhCN,
  ja,
  ko,
};

/**
 * Locale-aware string lookup.
 *
 * @param {string} key         A dotted string key from a dictionary.
 * @param {string} [locale]    Preferred locale.  Defaults to 'en'.
 * @returns {string}
 */
export function t(key, locale = 'en') {
  const dict = DICTIONARIES[locale] || DICTIONARIES.en;
  if (Object.prototype.hasOwnProperty.call(dict, key)) return dict[key];
  // Fallback: try English even if the preferred locale was non-en.
  const fallback = DICTIONARIES.en;
  if (fallback && Object.prototype.hasOwnProperty.call(fallback, key)) return fallback[key];
  return key;
}

/** @internal -- exposed for tests. */
export const __TEST__ = { DICTIONARIES };
